/* Route chunks that fail loudly instead of hanging.

   `lazy(() => import('./pages/Home'))` has two failure modes on a phone, and
   the plain form handles neither:

   · The request rejects. A deploy replaces every content-hashed chunk, so a
     tab that has been open across one — or a service worker still answering
     from a precache that names files the server no longer has — asks for a
     URL that is now a 404. React has nothing to render, the error reaches the
     root, and the *whole app* unmounts: no page, no tab bar, nothing to press.

   · The request never settles. A flaky connection can leave a chunk in flight
     indefinitely. React suspends, the skeleton stays up, and the screen sits
     there looking like a dark, empty page for as long as the user is willing
     to wait. Nothing has errored, so nothing recovers.

   So every route is loaded through here instead:

   · a rejection is retried, because most of them are one bad moment on a
     mobile connection rather than a missing file, and a silent second attempt
     is a better experience than any error state;

   · a request that has not settled by `TIMEOUT_MS` is treated as a rejection,
     which puts it back on the retry path and then, if it keeps failing, in
     front of the RouteBoundary — which knows how to fix the stale-deploy case;

   · what finally reaches React is a promise that either resolves with a module
     or rejects with something the boundary can act on. It never hangs. */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

/** Long enough not to fire on a genuinely slow connection that is working. */
export const TIMEOUT_MS = 15_000

/** Attempts after the first. Two is enough for a blip and short enough that a
 *  genuinely missing chunk reaches the recovery UI quickly. */
export const RETRIES = 2

/** Backs off a little between attempts — an instant retry tends to land in
 *  the same dead moment of connectivity that lost the first one. */
const RETRY_DELAY_MS = 400

interface ModuleWithDefault<T> {
  default: T
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** Rejects if `promise` has not settled in time. Note that this does not
 *  cancel the underlying request — it cannot, and it should not: if the fetch
 *  does eventually land, the browser has it cached and the retry below is a
 *  memory read. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`Timed out loading a route chunk after ${ms}ms`)),
      ms,
    )
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error: unknown) => {
        clearTimeout(timer)
        reject(error instanceof Error ? error : new Error(String(error)))
      },
    )
  })
}

/* eslint-disable no-await-in-loop -- a retry is sequential by definition:
   the whole point is to wait and see whether the previous attempt failed
   before deciding to make another one. Running them in parallel would fire
   every attempt at the same dead moment of connectivity. */
export async function loadWithRetry<T>(
  load: () => Promise<T>,
  retries = RETRIES,
  timeoutMs = TIMEOUT_MS,
): Promise<T> {
  let lastError: unknown

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await withTimeout(load(), timeoutMs)
    } catch (error) {
      lastError = error
      if (attempt < retries) await sleep(RETRY_DELAY_MS * (attempt + 1))
    }
  }

  throw lastError instanceof Error ? lastError : new Error(String(lastError))
}

/** Drop-in for `lazy(() => import(...))` on a route. */
export function lazyRoute<T extends ComponentType<Record<string, never>>>(
  load: () => Promise<ModuleWithDefault<T>>,
): LazyExoticComponent<T> {
  return lazy(() => loadWithRetry(load))
}
